import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/StateViews";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { GraduationCap, School, Loader2, Users, Award, Star, Clock, ClipboardCheck, UserCheck, Eye } from "lucide-react";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow">
      <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Icon className="h-4 w-4 text-teal-700" /> {label}</div>
      <div className="text-2xl font-heading font-black text-slate-900 arabic-numerals">{value ?? "—"}</div>
    </div>
  );
}

export default function FacultyHome() {
  const { user } = useAuth();
  const [data, setData] = useState({ supervised: [], college_match: [], university_match: [], university: "", college: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/faculty/students");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const allStudents = [...(data.supervised || []), ...(data.college_match || []), ...(data.university_match || [])];
  const totals = allStudents.reduce((acc, s) => ({
    hours: acc.hours + (s.total_hours || 0),
    tasks: acc.tasks + (s.tasks_done || 0),
    certified: acc.certified + (s.certificates_count || 0),
  }), { hours: 0, tasks: 0, certified: 0 });

  return (
    <div data-testid="faculty-home">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">إشراف أكاديمي</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">
            أهلاً {user?.name}
          </h1>
          <p className="mt-2 text-slate-600">
            متابعة طلاب <span className="font-semibold text-teal-700">{data.university}</span>{data.college ? <> — كلية <span className="font-semibold text-teal-700">{data.college}</span></> : null} في التدريب التعاوني.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <Stat icon={UserCheck} label="طلاب تحت إشرافي" value={(data.supervised || []).length} />
          <Stat icon={Users} label="طلاب كليتي/جامعتي" value={(data.college_match || []).length + (data.university_match || []).length} />
          <Stat icon={Clock} label="إجمالي ساعات التدريب" value={totals.hours} />
          <Stat icon={Award} label="شهادات صادرة" value={totals.certified} />
        </div>

        {loading ? (
          <div className="grid place-items-center py-12"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : (
          <div className="space-y-8">
            <StudentsGroup title="طلابي المُسنَدون (إشراف مباشر)" testid="grp-supervised" items={data.supervised || []} emptyText="لا يوجد طلاب مُسنَدون إليك حالياً. يمكنك إرسال طلب إشراف أو استقبال طلبات من صفحة «طلبات الإشراف»." />
            <StudentsGroup title="طلاب كليتي (نفس الكلية)" testid="grp-college" items={data.college_match || []} emptyText="لا يوجد طلاب من نفس الكلية." />
            <StudentsGroup title="طلاب جامعتي (كليات أخرى)" testid="grp-university" items={data.university_match || []} emptyText="لا يوجد طلاب إضافيون." />
          </div>
        )}
      </div>
  );
}

function StudentsGroup({ title, items, emptyText, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-cream-200 card-shadow overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-cream-200 flex items-center gap-2">
        <School className="h-5 w-5 text-teal-700" />
        <h3 className="font-heading font-bold text-slate-900">{title}</h3>
        <span className="ms-auto text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 arabic-numerals">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="p-4 text-sm text-slate-500">{emptyText}</div>
      ) : (
        <>
          {/* Mobile cards */}
          <ul className="md:hidden divide-y divide-cream-100" data-testid="faculty-mobile-list">
            {items.map((s) => (
              <li key={s.id} className="p-4" data-testid={`faculty-student-mobile-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-slate-900 truncate">{s.name}</div>
                    <div className="mt-0.5 text-xs text-slate-600 truncate">{s.major || "—"}</div>
                  </div>
                  {s.avg_evaluation != null && (
                    <span className="shrink-0 inline-flex items-center gap-1 font-bold text-amber-700 text-xs arabic-numerals">
                      <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> {s.avg_evaluation}
                    </span>
                  )}
                </div>
                <div className="mt-2 grid grid-cols-4 gap-1 text-[11px] text-slate-600">
                  <div><div className="text-slate-400">المعدل</div><div className="font-bold arabic-numerals">{s.gpa || "—"}</div></div>
                  <div><div className="text-slate-400">الساعات</div><div className="font-bold arabic-numerals">{s.total_hours}</div></div>
                  <div><div className="text-slate-400">المهام</div><div className="font-bold arabic-numerals">{s.tasks_done}</div></div>
                  <div><div className="text-slate-400">الشهادات</div><div className="font-bold arabic-numerals">{s.certificates_count}</div></div>
                </div>
                <div className="mt-3">
                  <Link
                    to={s.relation === "supervised" && s.application_id ? `/dashboard/faculty/journeys/${s.application_id}` : `/dashboard/faculty/student/${s.id}`}
                    className="inline-flex min-h-11 items-center gap-1.5 px-4 rounded-xl bg-teal-700 text-white text-xs font-bold active:bg-teal-800"
                    data-testid={`open-detail-mobile-${s.id}`}
                  >
                    <Eye className="h-3.5 w-3.5" /> التفاصيل
                  </Link>
                </div>
              </li>
            ))}
          </ul>
          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-50">
              <tr>
                <th className="text-start px-6 py-3 font-semibold text-slate-700">الاسم</th>
                <th className="text-start px-6 py-3 font-semibold text-slate-700">التخصص</th>
                <th className="text-start px-6 py-3 font-semibold text-slate-700">المعدل</th>
                <th className="text-start px-6 py-3 font-semibold text-slate-700">الفرص</th>
                <th className="text-start px-6 py-3 font-semibold text-slate-700">الساعات</th>
                <th className="text-start px-6 py-3 font-semibold text-slate-700">المهام</th>
                <th className="text-start px-6 py-3 font-semibold text-slate-700">التقييم</th>
                <th className="text-start px-6 py-3 font-semibold text-slate-700">الشهادات</th>
                <th className="text-start px-6 py-3 font-semibold text-slate-700"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-cream-100 hover:bg-cream-50/50" data-testid={`faculty-student-${s.id}`}>
                  <td className="px-6 py-3 font-semibold text-slate-900">{s.name}</td>
                  <td className="px-6 py-3 text-slate-700">{s.major || "—"}</td>
                  <td className="px-6 py-3 text-slate-700 arabic-numerals">{s.gpa || "—"}</td>
                  <td className="px-6 py-3 text-slate-700 arabic-numerals">{s.applications_count}</td>
                  <td className="px-6 py-3 text-slate-700 arabic-numerals font-bold">{s.total_hours}</td>
                  <td className="px-6 py-3 text-slate-700 arabic-numerals">{s.tasks_done}</td>
                  <td className="px-6 py-3">
                    {s.avg_evaluation != null ? (
                      <span className="inline-flex items-center gap-1 font-bold text-amber-700 arabic-numerals">
                        <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> {s.avg_evaluation}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-6 py-3 arabic-numerals">
                    <span className="inline-flex px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold text-xs">{s.certificates_count}</span>
                  </td>
                  <td className="px-6 py-3 text-end">
                    <Link
                      to={s.relation === "supervised" && s.application_id ? `/dashboard/faculty/journeys/${s.application_id}` : `/dashboard/faculty/student/${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-700 text-white text-xs font-bold hover:bg-teal-600"
                      data-testid={`open-detail-${s.id}`}
                    >
                      <Eye className="h-3.5 w-3.5" /> التفاصيل
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
